import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Select, Space, Tag, message,
    Row, Col, Progress, Statistic, Timeline, Badge, Empty
} from 'antd';
import {
    ReloadOutlined, CheckOutlined, WarningOutlined,
    StopOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';

const SectionCard = styled(Card)`
    margin-bottom: 16px;
`;

const UsageCard = styled(Card)`
    .usage-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
    }

    .scope-name {
        font-size: 16px;
        font-weight: 600;
        color: #002C57;
    }

    .plan-tag {
        margin-left: 8px;
    }
`;

const AlertCard = styled.div`
    background: ${props => {
        if (props.$level === 100) return '#fff2f0';
        if (props.$level === 90) return '#fffbe6';
        return '#e6f7ff';
    }};
    border: 1px solid ${props => {
        if (props.$level === 100) return '#ffa39e';
        if (props.$level === 90) return '#ffe58f';
        return '#91d5ff';
    }};
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;

    .alert-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .alert-title {
        font-weight: 600;
        color: ${props => {
            if (props.$level === 100) return '#cf1322';
            if (props.$level === 90) return '#d48806';
            return '#096dd9';
        }};
    }

    .alert-time {
        color: #999;
        font-size: 12px;
    }

    .alert-scope {
        margin-top: 8px;
        color: #666;
    }
`;

const FilterContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const UsageAlerts = () => {
    const [organizations, setOrganizations] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedScope, setSelectedScope] = useState({ type: null, id: null });
    const [usageStatus, setUsageStatus] = useState(null);
    const [usageHistory, setUsageHistory] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchOrganizations = async () => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/organizations`
            );
            if (response.success) {
                setOrganizations(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
        }
    };

    const fetchCustomers = async (orgId) => {
        if (!orgId) return;
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customers?orgId=${orgId}`
            );
            if (response.success) {
                setCustomers(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch customers:', error);
        }
    };

    const fetchUsageStatus = async () => {
        if (!selectedScope.type || !selectedScope.id) return;

        try {
            setLoading(true);
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/limits/tokens?scopeType=${selectedScope.type}&scopeId=${selectedScope.id}`
            );
            if (response.success) {
                setUsageStatus(response.data);
            }
        } catch (error) {
            message.error('Failed to fetch usage status');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsageHistory = async () => {
        if (!selectedScope.type || !selectedScope.id) return;

        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/limits/tokens/history?scopeType=${selectedScope.type}&scopeId=${selectedScope.id}&months=6`
            );
            if (response.success) {
                setUsageHistory(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch usage history:', error);
        }
    };

    const fetchAlerts = async () => {
        try {
            const params = new URLSearchParams();
            if (selectedScope.type) params.append('scopeType', selectedScope.type);
            if (selectedScope.id) params.append('scopeId', selectedScope.id);

            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/alerts?${params.toString()}`
            );
            if (response.success) {
                setAlerts(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch alerts:', error);
        }
    };

    const handleAcknowledgeAlert = async (alertId) => {
        try {
            const response = await execute(
                'POST',
                `${protectedResources.api.baseEndPoint}/ai-subscription/alerts/${alertId}/acknowledge`
            );
            if (response.success) {
                message.success('Alert acknowledged');
                fetchAlerts();
            }
        } catch (error) {
            message.error('Failed to acknowledge alert');
        }
    };

    useEffect(() => {
        fetchOrganizations();
        fetchAlerts();
    }, []);

    useEffect(() => {
        if (selectedScope.type && selectedScope.id) {
            fetchUsageStatus();
            fetchUsageHistory();
            fetchAlerts();
        }
    }, [selectedScope]);

    const getStatusColor = (status) => {
        const colors = {
            Normal: '#52c41a',
            Warn70: '#faad14',
            Warn90: '#fa8c16',
            Blocked: '#f5222d'
        };
        return colors[status] || '#1890ff';
    };

    const getStatusIcon = (status) => {
        if (status === 'Blocked') return <StopOutlined />;
        if (status === 'Warn90' || status === 'Warn70') return <WarningOutlined />;
        return <ThunderboltOutlined />;
    };

    const historyColumns = [
        {
            title: 'Month',
            dataIndex: 'YearMonth',
            key: 'yearMonth',
            render: (val) => dayjs(val + '-01').format('MMM YYYY')
        },
        {
            title: 'Tokens Used',
            dataIndex: 'TokensConsumed',
            key: 'tokensConsumed',
            render: (val) => val?.toLocaleString()
        },
        {
            title: 'Requests',
            dataIndex: 'RequestsCount',
            key: 'requestsCount',
            render: (val) => val?.toLocaleString()
        },
        {
            title: 'Last Updated',
            dataIndex: 'LastUpdatedOn',
            key: 'lastUpdated',
            render: (date) => dayjs(date).format('MMM DD, HH:mm')
        }
    ];

    const unacknowledgedAlerts = alerts.filter(a => !a.IsAcknowledged);

    return (
        <div>
            <Row gutter={16}>
                {/* Left Panel - Scope Selection & Usage */}
                <Col span={16}>
                    <SectionCard title="Usage Overview">
                        <FilterContainer>
                            <Select
                                placeholder="Select Organization"
                                style={{ width: 250 }}
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(val) => {
                                    setSelectedScope({ type: 'ORG', id: val });
                                    fetchCustomers(val);
                                    setCustomers([]);
                                }}
                                options={organizations.map(org => ({
                                    value: org.Org_ID,
                                    label: org.Org_Name
                                }))}
                            />
                            {customers.length > 0 && (
                                <Select
                                    placeholder="Select Customer (Optional)"
                                    style={{ width: 250 }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={(val) => {
                                        if (val) {
                                            setSelectedScope({ type: 'CUSTOMER', id: val });
                                        } else {
                                            setSelectedScope({ type: 'ORG', id: selectedScope.id });
                                        }
                                    }}
                                    options={customers.map(cust => ({
                                        value: cust.Customer_ID,
                                        label: cust.CustomerName
                                    }))}
                                />
                            )}
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    fetchUsageStatus();
                                    fetchUsageHistory();
                                    fetchAlerts();
                                }}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </FilterContainer>

                        {usageStatus ? (
                            usageStatus.hasSubscription ? (
                                <UsageCard>
                                    <div className="usage-header">
                                        <div>
                                            <span className="scope-name">
                                                {selectedScope.type === 'ORG' ? 'Organization' : 'Customer'} Usage
                                            </span>
                                            <Tag color="orange" className="plan-tag">
                                                {usageStatus.plan.planName}
                                            </Tag>
                                        </div>
                                        <Badge
                                            status={usageStatus.status === 'Normal' ? 'success' : usageStatus.status === 'Blocked' ? 'error' : 'warning'}
                                            text={usageStatus.status}
                                        />
                                    </div>

                                    <Row gutter={24}>
                                        <Col span={8}>
                                            <Statistic
                                                title="Tokens Used"
                                                value={usageStatus.tokens.consumed}
                                                suffix={`/ ${usageStatus.tokens.limit.toLocaleString()}`}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Remaining"
                                                value={usageStatus.tokens.remaining}
                                                valueStyle={{ color: getStatusColor(usageStatus.status) }}
                                            />
                                        </Col>
                                        <Col span={8}>
                                            <Statistic
                                                title="Usage"
                                                value={usageStatus.tokens.usagePercentage}
                                                suffix="%"
                                                prefix={getStatusIcon(usageStatus.status)}
                                                valueStyle={{ color: getStatusColor(usageStatus.status) }}
                                            />
                                        </Col>
                                    </Row>

                                    <div style={{ marginTop: 24 }}>
                                        <Progress
                                            percent={usageStatus.tokens.usagePercentage}
                                            strokeColor={getStatusColor(usageStatus.status)}
                                            status={usageStatus.status === 'Blocked' ? 'exception' : 'active'}
                                        />
                                    </div>
                                </UsageCard>
                            ) : (
                                <Empty description="No active subscription found for this scope" />
                            )
                        ) : (
                            <Empty description="Select an organization to view usage" />
                        )}
                    </SectionCard>

                    <SectionCard title="Usage History">
                        <Table
                            dataSource={usageHistory}
                            columns={historyColumns}
                            rowKey="UsageId"
                            loading={loading}
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Col>

                {/* Right Panel - Alerts */}
                <Col span={8}>
                    <SectionCard
                        title={
                            <Space>
                                Alerts
                                {unacknowledgedAlerts.length > 0 && (
                                    <Badge count={unacknowledgedAlerts.length} />
                                )}
                            </Space>
                        }
                    >
                        {alerts.length > 0 ? (
                            alerts.slice(0, 10).map(alert => (
                                <AlertCard key={alert.AlertId} $level={alert.ThresholdPct}>
                                    <div className="alert-header">
                                        <span className="alert-title">
                                            {alert.EventType === 'BLOCK' ? (
                                                <><StopOutlined /> Blocked at {alert.ThresholdPct}%</>
                                            ) : (
                                                <><WarningOutlined /> Warning at {alert.ThresholdPct}%</>
                                            )}
                                        </span>
                                        {!alert.IsAcknowledged && (
                                            <Button
                                                size="small"
                                                icon={<CheckOutlined />}
                                                onClick={() => handleAcknowledgeAlert(alert.AlertId)}
                                            >
                                                Ack
                                            </Button>
                                        )}
                                    </div>
                                    <div className="alert-scope">
                                        {alert.ScopeType}: ID {alert.ScopeId}
                                    </div>
                                    <div className="alert-time">
                                        {dayjs(alert.CreatedOn).format('MMM DD, YYYY HH:mm')}
                                    </div>
                                    {alert.IsAcknowledged && (
                                        <Tag color="default" style={{ marginTop: 8 }}>
                                            Acknowledged
                                        </Tag>
                                    )}
                                </AlertCard>
                            ))
                        ) : (
                            <Empty description="No alerts" />
                        )}
                    </SectionCard>

                    <SectionCard title="Alert Timeline">
                        <Timeline
                            items={alerts.slice(0, 5).map(alert => ({
                                color: alert.ThresholdPct === 100 ? 'red' : alert.ThresholdPct === 90 ? 'orange' : 'blue',
                                children: (
                                    <>
                                        <div>{alert.EventType} at {alert.ThresholdPct}%</div>
                                        <div style={{ fontSize: 12, color: '#999' }}>
                                            {dayjs(alert.CreatedOn).format('MMM DD, HH:mm')}
                                        </div>
                                    </>
                                )
                            }))}
                        />
                    </SectionCard>
                </Col>
            </Row>
        </div>
    );
};

export default UsageAlerts;
